import { useMemo, useRef, useState } from "react";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { Plus, Eye, UserCog, Pencil, Upload, Download, AlertTriangle, CheckCircle2, X, Mail, Search, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, BACKEND_URL } from "@/lib/api";
import { EditUserDialog, EXTRA_ROLES } from "@/components/admin/EditUserDialog";

// Age groups a user can be made Year Group Lead of.
const AGE_GROUPS = ["Adults","U18","U17","U16","U15","U14","U13","U12","U11","U10","U9","U8","U7","U6"];

export function UsersTab({
  users, setUsers, user, impersonate, nav, loadAll,
  openNewUser, setOpenNewUser, newUserForm, setNewUserForm,
  newUserResult, setNewUserResult, submitNewUser, setReassignSrc,
}) {
  const deleteUser = async (u) => {
    const typed = window.prompt(`Permanently delete ${u.name} (${u.email})?\n\nTheir bookings stay in the history with a scrubbed name. Teams they manage must be reassigned first.\n\nType DELETE to confirm.`);
    if (typed !== "DELETE") return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers?.(prev => (prev || []).filter(x => x.id !== u.id));
      toast.success(`${u.name} deleted`);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
  };
  // Year-group-lead editor state (local to this tab — parent doesn't need to know).
  const [ygEdit, setYgEdit] = useState(null); // the user being edited
  const [ygSelection, setYgSelection] = useState([]);
  const [ygSaving, setYgSaving] = useState(false);

  // ---- Bulk CSV import ----
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // { valid, errors, valid_count, error_count }
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null); // { created, skipped, ... }
  const [sendWelcome, setSendWelcome] = useState(true);
  const fileInputRef = useRef(null);

  const resetImport = () => {
    setImportFile(null); setImportPreview(null); setImportResult(null); setImportBusy(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const previewImport = async (file) => {
    setImportBusy(true);
    setImportPreview(null); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/users/bulk-preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportPreview(data);
      if (data.valid_count === 0) toast.error("No valid rows in that file — fix the errors below and re-upload");
      else toast.success(`${data.valid_count} row${data.valid_count === 1 ? "" : "s"} ready to import`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read that CSV");
    } finally { setImportBusy(false); }
  };

  const commitImport = async () => {
    if (!importFile) return;
    setImportBusy(true);
    try {
      const fd = new FormData(); fd.append("file", importFile);
      const { data } = await api.post(`/admin/users/bulk-commit?send_welcome_email=${sendWelcome}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`Created ${data.created_count} account${data.created_count === 1 ? "" : "s"}`);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally { setImportBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/users/bulk-template`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("template download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "elsenham-users-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download the template"); }
  };

  // ---- Bulk resend welcome invites ----
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [editUser, setEditUser] = useState(null);
  const [showPermCols, setShowPermCols] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null); // { total, sent, failed, results: [...] }

  // ---- Filters ----
  // Free-text search across name / email / phone, plus dropdowns for role,
  // sign-in status, extra permissions, and team association. Filters are
  // client-side (the /admin/users endpoint already returns the full list)
  // and drive a `useMemo`d `filtered` array — the select-all + never-signed-
  // in shortcut both operate on the filtered set so bulk actions match what
  // the admin can actually see.
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [signInFilter, setSignInFilter] = useState("all");
  const [permFilter, setPermFilter] = useState("all");
  const [teamFilter, setTeamFilter] = useState("all");
  const anyFilterActive = q.trim() !== "" || roleFilter !== "all" || signInFilter !== "all" || permFilter !== "all" || teamFilter !== "all";
  const clearFilters = () => { setQ(""); setRoleFilter("all"); setSignInFilter("all"); setPermFilter("all"); setTeamFilter("all"); };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (users || []).filter(u => {
      if (needle) {
        const hay = `${u.name || ""} ${u.email || ""} ${u.phone || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (signInFilter === "never" && u.last_login_at) return false;
      if (signInFilter === "signed_in" && !u.last_login_at) return false;
      if (permFilter !== "all") {
        const isYgl = (u.year_group_lead_ages || []).length > 0;
        if (permFilter === "kit_admin"         && !(u.is_kit_admin || u.role === "admin")) return false;
        if (permFilter === "expense_approver"  && !(u.is_expense_approver || u.role === "admin")) return false;
        if (permFilter === "treasurer"         && !(u.is_treasurer || u.role === "admin")) return false;
        if (permFilter === "club_secretary"    && !u.is_club_secretary) return false;
        if (permFilter === "year_group_lead"   && !isYgl) return false;
      }
      if (teamFilter === "has_team" && !(u.teams && u.teams.length > 0)) return false;
      if (teamFilter === "no_team"  &&  (u.teams && u.teams.length > 0)) return false;
      return true;
    });
  }, [users, q, roleFilter, signInFilter, permFilter, teamFilter]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectNeverSignedIn = () => {
    const ids = filtered.filter(u => !u.last_login_at && u.role !== "admin").map(u => u.id);
    setSelectedIds(new Set(ids));
    if (ids.length === 0) toast.info("No never-signed-in accounts in the current filter");
    else toast.success(`Selected ${ids.length} account${ids.length === 1 ? "" : "s"} that never signed in`);
  };
  const runBulkResend = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Send a fresh invite (new temp password) to ${selectedIds.size} user${selectedIds.size === 1 ? "" : "s"}?\n\nAny existing passwords for these accounts will be replaced.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/users/bulk-resend-welcome", {
        scope: "selected", user_ids: Array.from(selectedIds),
      });
      setBulkResult(data);
      toast.success(`${data.sent}/${data.total} invite email${data.total === 1 ? "" : "s"} sent`);
      clearSelection();
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk resend failed"); }
    finally { setBulkBusy(false); }
  };


  const openYgEditor = (u) => {
    setYgEdit(u);
    setYgSelection([...(u.year_group_lead_ages || [])]);
  };
  const saveYg = async () => {
    if (!ygEdit) return;
    setYgSaving(true);
    try {
      await api.put(`/admin/users/${ygEdit.id}/year-group-lead`, { year_group_lead_ages: ygSelection });
      toast.success(ygSelection.length ? `${ygEdit.name} · Lead of ${ygSelection.join(", ")}` : `${ygEdit.name} · no longer a Year Group Lead`);
      setYgEdit(null);
      loadAll();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setYgSaving(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="text-sm opacity-70">
          {users.length} account{users.length === 1 ? "" : "s"}
          {selectedIds.size > 0 && (
            <span className="ml-3 text-[#83B68B] font-semibold" data-testid="users-selected-count">
              · {selectedIds.size} selected
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={selectNeverSignedIn}
            data-testid="admin-select-never-signed-in"
            className="px-3 py-2.5 rounded-md border border-amber-400/50 text-amber-300 hover:bg-amber-500/10 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-display font-bold"
          >
            <Mail className="w-4 h-4" /> Select never signed in
          </button>
          {selectedIds.size > 0 && (
            <>
              <button
                onClick={runBulkResend}
                disabled={bulkBusy}
                data-testid="admin-bulk-resend-btn"
                className="btn-volt px-4 py-2.5 rounded-md inline-flex items-center gap-2 disabled:opacity-40"
              >
                <Mail className="w-4 h-4" /> {bulkBusy ? "Sending…" : `Resend to selected (${selectedIds.size})`}
              </button>
              <button
                onClick={clearSelection}
                data-testid="admin-clear-selection"
                className="px-3 py-2.5 rounded-md border border-border hover:border-white/40 text-xs tracking-widest uppercase"
              >
                Clear
              </button>
            </>
          )}
          <button
            onClick={() => { resetImport(); setImportOpen(true); }}
            data-testid="admin-import-users-btn"
            className="px-4 py-2.5 rounded-md border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B]/10 inline-flex items-center gap-2 text-xs tracking-widest uppercase font-display font-bold"
          >
            <Upload className="w-4 h-4" /> Import CSV
          </button>
          <button
            onClick={() => { setNewUserForm({ email: "", name: "", role: "coach", phone: "", password: "", force_password_change: true, send_welcome_email: true }); setNewUserResult(null); setOpenNewUser(true); }}
            data-testid="admin-new-user-btn"
            className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> New user
          </button>
        </div>
      </div>
      <div className="card-tactical rounded-md p-4" data-testid="users-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[10px] tracking-widest uppercase opacity-60">Search</Label>
            <div className="relative mt-1.5">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 opacity-40 pointer-events-none" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Name, email or phone…"
                data-testid="users-filter-search"
                className="pl-8"
              />
            </div>
          </div>
          <div className="w-40">
            <Label className="text-[10px] tracking-widest uppercase opacity-60">Role</Label>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger data-testid="users-filter-role" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="coach">Coach</SelectItem>
                <SelectItem value="member">Member</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Label className="text-[10px] tracking-widest uppercase opacity-60">Extra permission</Label>
            <Select value={permFilter} onValueChange={setPermFilter}>
              <SelectTrigger data-testid="users-filter-perm" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="kit_admin">Kit Admin</SelectItem>
                <SelectItem value="expense_approver">Expense Approver</SelectItem>
                <SelectItem value="treasurer">Treasurer</SelectItem>
                <SelectItem value="club_secretary">Club Secretary</SelectItem>
                <SelectItem value="year_group_lead">Year Group Lead</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-36">
            <Label className="text-[10px] tracking-widest uppercase opacity-60">Teams</Label>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger data-testid="users-filter-team" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="has_team">Has team</SelectItem>
                <SelectItem value="no_team">No team</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label className="text-[10px] tracking-widest uppercase opacity-60">Sign-in status</Label>
            <Select value={signInFilter} onValueChange={setSignInFilter}>
              <SelectTrigger data-testid="users-filter-signin" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any</SelectItem>
                <SelectItem value="signed_in">Has signed in</SelectItem>
                <SelectItem value="never">Never signed in</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {anyFilterActive && (
            <button
              type="button"
              onClick={clearFilters}
              data-testid="users-filter-clear"
              className="px-3 py-2 rounded-md border border-border hover:border-white/40 text-[10px] tracking-widest uppercase font-display font-bold inline-flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          )}
        </div>
        <div className="mt-3 text-[11px] opacity-60 flex flex-wrap items-center gap-3" data-testid="users-filter-count">
          <span>Showing <strong className="text-white">{filtered.length}</strong> of {users.length} user{users.length === 1 ? "" : "s"}
          {selectedIds.size > 0 && <> · <span className="text-[#83B68B]">{selectedIds.size} selected</span></>}</span>
          <label className="inline-flex items-center gap-2 cursor-pointer ml-auto"><input type="checkbox" checked={showPermCols} onChange={e => setShowPermCols(e.target.checked)} data-testid="users-show-perm-cols" className="accent-[#83B68B]" /> Show permission checkboxes</label>
        </div>
      </div>

      <div className="card-tactical rounded-md overflow-hidden overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
            <tr>
              <th className="text-left px-3 py-3 w-8">
                <input
                  type="checkbox"
                  data-testid="users-select-all"
                  checked={filtered.length > 0 && filtered.every(u => selectedIds.has(u.id))}
                  onChange={(e) => setSelectedIds(e.target.checked ? new Set(filtered.map(u => u.id)) : new Set())}
                  className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                  title="Select all in the current filter"
                />
              </th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Phone</th>
              <th className="text-left px-4 py-3">Role</th>
              <th className="text-left px-4 py-3">Extra roles</th>
              {showPermCols && <><th className="text-left px-4 py-3">Kit Admin</th>
              <th className="text-left px-4 py-3">Expense Approver</th>
              <th className="text-left px-4 py-3">Treasurer</th>
              <th className="text-left px-4 py-3">Welfare Officer</th>
              <th className="text-left px-4 py-3">Club Secretary</th>
              <th className="text-left px-4 py-3">Ref Coordinator</th>
              <th className="text-left px-4 py-3">Year Group Lead</th></>}
              <th className="text-left px-4 py-3">Team(s)</th>
              <th className="text-left px-4 py-3">Last login</th>
              <th className="text-left px-4 py-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={13} className="px-4 py-10 text-center opacity-60" data-testid="users-filter-empty">
                  {anyFilterActive
                    ? <>No users match those filters. <button onClick={clearFilters} className="text-[#83B68B] hover:underline">Clear filters</button></>
                    : "No users yet."}
                </td>
              </tr>
            ) : filtered.map(u => (
              <tr key={u.id} className={`border-t border-border ${selectedIds.has(u.id) ? "bg-[#83B68B]/5" : ""}`}>
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(u.id)}
                    onChange={() => toggleSelect(u.id)}
                    data-testid={`user-select-${u.id}`}
                    className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => setEditUser(u)} data-testid={`user-edit-${u.id}`} title="Edit details, email, role and permissions" className="p-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]"><Pencil className="w-3 h-3" /></button>
                    {u.id !== user?.id && (
                      <button type="button" data-testid={`delete-user-${u.id}`} title="Permanently delete this user"
                        onClick={() => deleteUser(u)}
                        className="p-1.5 rounded border border-border hover:border-red-500 hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                    )}
                    {u.id !== user?.id && u.role !== "admin" && (user?.role === "admin" || (user?.permissions || []).includes("users.view_as")) && (
                      <button type="button" data-testid={`impersonate-${u.id}`} title="See the app exactly as this user does (read-only, 30 min)"
                        onClick={async () => {
                          try {
                            await impersonate(u.id);
                            window.location.href = u.role === "treasurer" ? "/app/finance" : "/app/calendar";
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        className="p-1.5 rounded border border-border hover:border-amber-400 hover:text-amber-300"><Eye className="w-3 h-3" /></button>
                    )}
                    <span>{u.name}</span>
                  </div>
                  {u.fan_number && <div className="text-[10px] tracking-widest uppercase opacity-50 mt-0.5">FAN {u.fan_number}</div>}
                  {!u.last_login_at && (
                    <span
                      className="ml-2 text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-400/40"
                      data-testid={`user-never-signed-in-${u.id}`}
                      title="This account has never signed in"
                    >
                      Never signed in
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 opacity-80">{u.email}</td>
                <td className="px-4 py-3 opacity-80">{u.phone || "—"}</td>
                <td className="px-4 py-3">
                  <Select value={u.role} onValueChange={async (v)=>{
                    try {
                      await api.put(`/admin/users/${u.id}/role`, { role: v });
                      toast.success(`${u.name} → ${v}`);
                      loadAll();
                    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                  }}>
                    <SelectTrigger data-testid={`user-role-${u.id}`} className="w-32 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="coach">Coach</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="vendor">Vendor (view-only calendar)</SelectItem>
                      <SelectItem value="treasurer">Treasurer (finance only)</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1 max-w-[220px]" data-testid={`user-extra-roles-${u.id}`}>
                    {u.role === "admin" && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border border-border opacity-50">All (admin)</span>}
                    {EXTRA_ROLES.filter(([k]) => u[k] === true).map(([k, label]) => <span key={k} className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30">{label}</span>)}
                    {(u.year_group_lead_ages || []).length > 0 && <span className="text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30">YGL {(u.year_group_lead_ages || []).join(" ")}</span>}
                    {u.role !== "admin" && !EXTRA_ROLES.some(([k]) => u[k] === true) && !(u.year_group_lead_ages || []).length && <span className="opacity-40 text-xs">—</span>}
                  </div>
                </td>
                {showPermCols && <><td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-[10px] tracking-widest uppercase opacity-50" title="Admins have all kit-admin powers">Admin</span>
                  ) : (
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-kit-admin-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={u.is_kit_admin === true}
                        onChange={async (e) => {
                          const v = e.target.checked;
                          try {
                            await api.put(`/admin/users/${u.id}/kit-admin`, { is_kit_admin: v });
                            toast.success(`${u.name} · Kit Admin ${v ? "granted" : "removed"}`);
                            loadAll();
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                      />
                      <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_kit_admin ? "Yes" : "No"}</span>
                    </label>
                  )}
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Send ${u.name} a fresh invite email with a new temporary password?\n\nAny existing password will be replaced and they'll be forced to reset on next login.`)) return;
                      try {
                        const { data } = await api.post(`/admin/users/${u.id}/resend-welcome`);
                        if (data.email_sent) {
                          toast.success(`Invite resent to ${data.email} · Temp password: ${data.temp_password}`, { duration: 15000 });
                        } else {
                          toast.warning(`Email failed to send — share this temp password manually: ${data.temp_password}`, { duration: 30000 });
                        }
                      } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Resend failed"); }
                    }}
                    data-testid={`resend-invite-${u.id}`}
                    title="Regenerate a temporary password and email the welcome invite again"
                    className="ml-2 text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1 align-middle"
                  >
                    <Mail className="w-3 h-3" /> Resend invite
                  </button>
                  {u.id !== user?.id && (
                    <button
                      onClick={() => deleteUser(u)}
                      data-testid={`delete-user-perm-${u.id}`}
                      title="Permanently delete this user"
                      className="ml-2 text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-red-500 hover:text-red-400 inline-flex items-center gap-1 align-middle"
                    >
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-[10px] tracking-widest uppercase opacity-50" title="Admins are implicitly Expense Approver">Admin</span>
                  ) : (
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-expense-approver-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={u.is_expense_approver === true}
                        onChange={async (e) => {
                          const v = e.target.checked;
                          try {
                            await api.put(`/admin/users/${u.id}/expense-approver`, { is_expense_approver: v });
                            toast.success(`${u.name} · Expense Approver ${v ? "granted" : "removed"}`);
                            loadAll();
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                      />
                      <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_expense_approver ? "Yes" : "No"}</span>
                    </label>
                  )}
                </td>
                <td className="px-4 py-3">
                  {u.role === "admin" ? (
                    <span className="text-[10px] tracking-widest uppercase opacity-50" title="Admins are implicitly Treasurer">Admin</span>
                  ) : (
                    <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-treasurer-${u.id}`}>
                      <input
                        type="checkbox"
                        checked={u.is_treasurer === true}
                        onChange={async (e) => {
                          const v = e.target.checked;
                          try {
                            await api.put(`/admin/users/${u.id}/treasurer`, { is_treasurer: v });
                            toast.success(`${u.name} · Treasurer ${v ? "granted" : "removed"}`);
                            loadAll();
                          } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                        }}
                        className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                      />
                      <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_treasurer ? "Yes" : "No"}</span>
                    </label>
                  )}
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-welfare-officer-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={u.is_welfare_officer === true}
                      onChange={async (e) => {
                        const v = e.target.checked;
                        try {
                          await api.put(`/admin/users/${u.id}/welfare-officer`, { is_welfare_officer: v });
                          toast.success(`${u.name} · Welfare Officer ${v ? "granted" : "removed"}`);
                          loadAll();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                    />
                    <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_welfare_officer ? "CWO" : "No"}</span>
                  </label>
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-club-secretary-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={u.is_club_secretary === true}
                      onChange={async (e) => {
                        const v = e.target.checked;
                        try {
                          await api.put(`/admin/users/${u.id}/club-secretary`, { is_club_secretary: v });
                          toast.success(`${u.name} · Club Secretary ${v ? "granted" : "removed"}`);
                          loadAll();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                    />
                    <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_club_secretary ? "Yes" : "No"}</span>
                  </label>
                </td>
                <td className="px-4 py-3">
                  <label className="inline-flex items-center gap-2 cursor-pointer" data-testid={`user-ref-coordinator-${u.id}`}>
                    <input
                      type="checkbox"
                      checked={u.is_referee_coordinator === true}
                      onChange={async (e) => {
                        const v = e.target.checked;
                        try {
                          await api.put(`/admin/users/${u.id}/referee-coordinator`, { is_referee_coordinator: v });
                          toast.success(`${u.name} · Referee Coordinator ${v ? "granted" : "removed"}`);
                          loadAll();
                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      }}
                      className="w-4 h-4 accent-[#83B68B] cursor-pointer"
                    />
                    <span className="text-[10px] tracking-widest uppercase opacity-70">{u.is_referee_coordinator ? "Yes" : "No"}</span>
                  </label>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1" data-testid={`user-ygl-${u.id}`}>
                    {(u.year_group_lead_ages || []).length === 0 ? (
                      <span className="opacity-40 text-xs">—</span>
                    ) : (
                      (u.year_group_lead_ages || []).map(a => (
                        <span key={a} className="text-[10px] tracking-widest uppercase px-1.5 py-0.5 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30">{a}</span>
                      ))
                    )}
                    <button
                      onClick={() => openYgEditor(u)}
                      data-testid={`user-ygl-edit-${u.id}`}
                      title="Set year groups this user leads"
                      className="ml-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>
                  </div>
                </td></>}
                <td className="px-4 py-3">
                  {u.teams && u.teams.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      {u.teams.map(t => (
                        <span key={t.id} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30">{t.name}</span>
                      ))}
                      <button
                        onClick={()=>setReassignSrc(u)}
                        data-testid={`user-reassign-teams-${u.id}`}
                        title="Reassign all of this coach's teams to another user"
                        className="ml-1 text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1"
                      >
                        <UserCog className="w-3 h-3" /> Reassign
                      </button>
                    </div>
                  ) : <span className="opacity-40">—</span>}
                </td>
                <td className="px-4 py-3 opacity-70 whitespace-nowrap" data-testid={`user-last-login-${u.id}`}>
                  {u.last_login_at ? (
                    <span title={format(parseISO(u.last_login_at), "d MMM yyyy 'at' HH:mm")}>
                      {formatDistanceToNow(parseISO(u.last_login_at), { addSuffix: true })}
                    </span>
                  ) : (
                    <span className="text-[10px] tracking-widest uppercase opacity-50">Never</span>
                  )}
                </td>
                <td className="px-4 py-3 opacity-60">{format(parseISO(u.created_at), "d MMM yyyy")}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {editUser && <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={loadAll} />}
      </div>

      <Dialog open={!!ygEdit} onOpenChange={(v)=>{ if (!v) setYgEdit(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Year Group Lead</DialogTitle>
            <DialogDescription className="text-xs opacity-60 leading-relaxed">
              Pick every age group this user is Year Group Lead for. Leads can add, amend and cancel bookings for any team in the age groups you tick — regardless of who owns the team.
            </DialogDescription>
          </DialogHeader>
          {ygEdit && (
            <div className="space-y-4" data-testid="ygl-editor-dialog">
              <div className="text-sm">
                <div className="font-medium">{ygEdit.name}</div>
                <div className="opacity-60 text-xs">{ygEdit.email}</div>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {AGE_GROUPS.map(a => {
                  const on = ygSelection.includes(a);
                  return (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setYgSelection(cur => on ? cur.filter(x => x !== a) : [...cur, a])}
                      data-testid={`ygl-tick-${a}`}
                      className={`py-2 rounded text-xs font-display tracking-widest uppercase transition-colors ${
                        on ? "bg-[#83B68B] text-white" : "bg-muted border border-border opacity-80 hover:border-[#83B68B]"
                      }`}
                    >{a}</button>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setYgEdit(null)} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Cancel</button>
                <button type="button" onClick={saveYg} disabled={ygSaving} data-testid="ygl-save-btn" className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40">
                  {ygSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openNewUser} onOpenChange={(v)=>{setOpenNewUser(v); if(!v) setNewUserResult(null);}}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display tracking-tight text-2xl">{newUserResult ? "Account created" : "Add user"}</DialogTitle></DialogHeader>
          {newUserResult ? (
            <div className="space-y-4">
              <div className="text-sm">
                <span className="font-medium">{newUserResult.name}</span> · <span className="opacity-70">{newUserResult.email}</span> · <span className="text-[#83B68B]">{newUserResult.role}</span>
              </div>
              {newUserResult.welcome_email_sent ? (
                <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 p-3 text-xs text-[#83B68B] flex items-center gap-2" data-testid="admin-new-user-email-status">
                  <span>✓</span> Welcome email sent to {newUserResult.email}
                </div>
              ) : newUserForm.send_welcome_email ? (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-500" data-testid="admin-new-user-email-status">
                  Welcome email couldn't be sent — check the email log or share credentials manually.
                </div>
              ) : null}
              {newUserResult.temp_password ? (
                <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/10 p-4 space-y-2">
                  <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Temporary password (share once)</div>
                  <div className="font-mono text-lg select-all" data-testid="admin-new-user-temp-password">{newUserResult.temp_password}</div>
                  <div className="text-xs opacity-70">They'll be asked to change this on first sign-in.</div>
                </div>
              ) : (
                <div className="text-xs opacity-70">You supplied a password — it hasn't been shown here. Share it with the user via a secure channel.</div>
              )}
              <button onClick={()=>{ setOpenNewUser(false); setNewUserResult(null); }} className="btn-volt w-full py-3 rounded-md" data-testid="admin-new-user-done">Done</button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Name</Label>
                <Input value={newUserForm.name} onChange={e=>setNewUserForm({...newUserForm, name: e.target.value})} className="mt-2" data-testid="admin-new-user-name" placeholder="Coach Jane Smith" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Email</Label>
                <Input type="email" value={newUserForm.email} onChange={e=>setNewUserForm({...newUserForm, email: e.target.value})} className="mt-2" data-testid="admin-new-user-email" placeholder="jane@example.com" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Phone (optional)</Label>
                <Input value={newUserForm.phone} onChange={e=>setNewUserForm({...newUserForm, phone: e.target.value})} className="mt-2" data-testid="admin-new-user-phone" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Role</Label>
                <Select value={newUserForm.role} onValueChange={v=>setNewUserForm({...newUserForm, role: v})}>
                  <SelectTrigger data-testid="admin-new-user-role" className="mt-2"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin — full access</SelectItem>
                    <SelectItem value="coach">Coach — book slots</SelectItem>
                    <SelectItem value="member">Member — view only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Password (optional)</Label>
                <Input type="text" value={newUserForm.password} onChange={e=>setNewUserForm({...newUserForm, password: e.target.value})} className="mt-2" data-testid="admin-new-user-password" placeholder="Leave blank to auto-generate" />
                <div className="text-[10px] opacity-60 mt-1">Blank = we generate a shareable temp password.</div>
              </div>
              <label className="flex items-center gap-2 text-sm select-none">
                <input type="checkbox" checked={newUserForm.force_password_change} onChange={e=>setNewUserForm({...newUserForm, force_password_change: e.target.checked})} data-testid="admin-new-user-force-change" />
                <span>Ask them to change password on first sign-in</span>
              </label>
              <label className="flex items-center gap-2 text-sm select-none">
                <input type="checkbox" checked={newUserForm.send_welcome_email} onChange={e=>setNewUserForm({...newUserForm, send_welcome_email: e.target.checked})} data-testid="admin-new-user-send-email" />
                <span>Email them a welcome message with sign-in link</span>
              </label>
              <button onClick={submitNewUser} className="btn-volt w-full py-3 rounded-md" data-testid="admin-new-user-submit">Create account</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ---- Bulk CSV import ---- */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) resetImport(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-import-users-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl inline-flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#83B68B]" /> Import users from CSV
            </DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Upload a CSV with one user per row. We'll validate it first, show you any problems, then only create the good rows when you commit.
            </DialogDescription>
          </DialogHeader>

          {!importResult && (
            <div className="space-y-4">
              <div className="rounded-md border border-border p-4 bg-muted/30 text-sm space-y-2">
                <div className="text-[10px] tracking-widest uppercase opacity-70">Required columns</div>
                <div><code className="text-[#83B68B]">email</code>, <code className="text-[#83B68B]">name</code></div>
                <div className="text-[10px] tracking-widest uppercase opacity-70 mt-3">Optional columns</div>
                <div className="text-xs opacity-80">
                  <code>role</code> (admin · coach · member — default member) · <code>phone</code> · <code>is_kit_admin</code> · <code>is_expense_approver</code> · <code>is_treasurer</code> · <code>year_group_lead_ages</code> (comma-separated, e.g. <code>U8,U9</code>)
                </div>
                <button onClick={downloadTemplate} data-testid="admin-import-download-template" className="mt-2 text-xs text-[#83B68B] hover:underline inline-flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> Download template CSV
                </button>
              </div>

              <div>
                <Label className="text-xs uppercase tracking-widest opacity-70">Choose a CSV file</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    setImportFile(f || null);
                    if (f) previewImport(f);
                  }}
                  data-testid="admin-import-file-input"
                  className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:tracking-widest file:uppercase file:font-display file:font-bold file:bg-[#83B68B] file:text-white hover:file:bg-[#6EA579] file:cursor-pointer cursor-pointer"
                />
              </div>

              {importBusy && !importPreview && (
                <div className="text-center py-4 text-sm opacity-70">Reading your file…</div>
              )}

              {importPreview && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="flex-1 rounded-md border border-[#83B68B]/40 bg-[#83B68B]/10 p-3">
                      <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Ready to import</div>
                      <div className="font-display font-black text-2xl mt-1">{importPreview.valid_count}</div>
                    </div>
                    <div className={`flex-1 rounded-md border p-3 ${importPreview.error_count ? "border-red-500/40 bg-red-500/10" : "border-border bg-muted/30"}`}>
                      <div className={`text-[10px] tracking-widest uppercase ${importPreview.error_count ? "text-red-400" : "opacity-60"}`}>Blocked</div>
                      <div className="font-display font-black text-2xl mt-1">{importPreview.error_count}</div>
                    </div>
                  </div>

                  {importPreview.errors.length > 0 && (
                    <div className="rounded-md border border-red-500/30 overflow-hidden">
                      <div className="bg-red-500/10 px-3 py-2 text-[10px] tracking-widest uppercase text-red-400 inline-flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5" /> Rows we can't import
                      </div>
                      <div className="max-h-52 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-muted/50 text-[10px] tracking-widest uppercase opacity-70">
                            <tr>
                              <th className="text-left px-3 py-1.5 w-12">Row</th>
                              <th className="text-left px-3 py-1.5">Email</th>
                              <th className="text-left px-3 py-1.5">Problem(s)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importPreview.errors.map((r, i) => (
                              <tr key={`${r.row_number}-${i}`} className="border-t border-border">
                                <td className="px-3 py-1.5 opacity-60">{r.row_number}</td>
                                <td className="px-3 py-1.5 font-mono">{r.email || "—"}</td>
                                <td className="px-3 py-1.5 text-red-400">{r.errors.join("; ")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {importPreview.valid.length > 0 && (
                    <div className="rounded-md border border-border overflow-hidden">
                      <div className="bg-muted/30 px-3 py-2 text-[10px] tracking-widest uppercase opacity-70 inline-flex items-center gap-2">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B]" /> Rows we'll create
                      </div>
                      <div className="max-h-52 overflow-y-auto text-xs">
                        <table className="w-full">
                          <thead className="bg-muted/50 text-[10px] tracking-widest uppercase opacity-70">
                            <tr>
                              <th className="text-left px-3 py-1.5 w-12">Row</th>
                              <th className="text-left px-3 py-1.5">Name</th>
                              <th className="text-left px-3 py-1.5">Email</th>
                              <th className="text-left px-3 py-1.5">Role</th>
                              <th className="text-left px-3 py-1.5">Extras</th>
                            </tr>
                          </thead>
                          <tbody>
                            {importPreview.valid.map((r, i) => (
                              <tr key={r.email || r.row_number} className="border-t border-border">
                                <td className="px-3 py-1.5 opacity-60">{r.row_number}</td>
                                <td className="px-3 py-1.5 font-medium">{r.name}</td>
                                <td className="px-3 py-1.5 font-mono opacity-80">{r.email}</td>
                                <td className="px-3 py-1.5 text-[#83B68B]">{r.role}</td>
                                <td className="px-3 py-1.5 opacity-70">
                                  {[
                                    r.is_kit_admin && "Kit",
                                    r.is_expense_approver && "Approver",
                                    r.is_treasurer && "Treasurer",
                                    r.year_group_lead_ages.length && `Lead: ${r.year_group_lead_ages.join(",")}`,
                                  ].filter(Boolean).join(" · ") || "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <label className="flex items-center gap-2 text-sm select-none">
                    <input type="checkbox" checked={sendWelcome} onChange={e => setSendWelcome(e.target.checked)} data-testid="admin-import-send-welcome" />
                    <span>Email each new user a welcome message with their temporary password</span>
                  </label>

                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => setImportOpen(false)}
                      className="flex-1 py-3 rounded-md border border-border text-xs tracking-widest uppercase font-display font-bold hover:border-white/40"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={commitImport}
                      disabled={importBusy || importPreview.valid_count === 0}
                      data-testid="admin-import-commit-btn"
                      className="btn-volt flex-1 py-3 rounded-md disabled:opacity-40 inline-flex items-center justify-center gap-2"
                    >
                      {importBusy ? "Importing…" : `Import ${importPreview.valid_count} user${importPreview.valid_count === 1 ? "" : "s"}`}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {importResult && (
            <div className="space-y-4" data-testid="admin-import-result">
              <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/10 p-4">
                <div className="flex items-center gap-2 text-[#83B68B]">
                  <CheckCircle2 className="w-5 h-5" />
                  <div className="font-display font-black text-xl">
                    {importResult.created_count} account{importResult.created_count === 1 ? "" : "s"} created
                  </div>
                </div>
                {importResult.skipped_count > 0 && (
                  <div className="mt-2 text-xs opacity-80">
                    {importResult.skipped_count} row{importResult.skipped_count === 1 ? " was" : "s were"} skipped due to validation errors.
                  </div>
                )}
                {importResult.welcome_email_failures?.length > 0 && (
                  <div className="mt-2 text-xs text-amber-400">
                    Note: {importResult.welcome_email_failures.length} welcome email{importResult.welcome_email_failures.length === 1 ? "" : "s"} couldn't be sent. Temp passwords are still visible below — share manually.
                  </div>
                )}
              </div>

              <div className="rounded-md border border-border overflow-hidden">
                <div className="bg-muted/50 px-3 py-2 text-[10px] tracking-widest uppercase opacity-70">Temporary passwords (share once each)</div>
                <div className="max-h-72 overflow-y-auto text-xs">
                  <table className="w-full">
                    <thead className="bg-muted/30 text-[10px] tracking-widest uppercase opacity-70">
                      <tr>
                        <th className="text-left px-3 py-1.5">Name</th>
                        <th className="text-left px-3 py-1.5">Email</th>
                        <th className="text-left px-3 py-1.5">Role</th>
                        <th className="text-left px-3 py-1.5">Temp password</th>
                        <th className="text-left px-3 py-1.5">Welcome sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.created.map((u, i) => (
                        <tr key={u.id || u.email} className="border-t border-border">
                          <td className="px-3 py-1.5 font-medium">{u.name}</td>
                          <td className="px-3 py-1.5 font-mono opacity-80">{u.email}</td>
                          <td className="px-3 py-1.5 text-[#83B68B]">{u.role}</td>
                          <td className="px-3 py-1.5 font-mono select-all">{u.temp_password}</td>
                          <td className="px-3 py-1.5">{u.welcome_email_sent ? <CheckCircle2 className="w-3.5 h-3.5 text-[#83B68B]" /> : <X className="w-3.5 h-3.5 text-amber-400" />}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <button onClick={() => setImportOpen(false)} className="btn-volt w-full py-3 rounded-md" data-testid="admin-import-done-btn">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk resend results — per-user temp passwords so admins can chase failed
          deliveries out-of-band without waiting on Resend to succeed. */}
      <Dialog open={!!bulkResult} onOpenChange={(v)=>{ if (!v) setBulkResult(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="bulk-resend-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Invites resent</DialogTitle>
            <DialogDescription className="text-xs opacity-70">
              Each account below now has a fresh temporary password (forced-reset on next sign-in). Anywhere the email failed, copy the temp password and share it another way.
            </DialogDescription>
          </DialogHeader>
          {bulkResult && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-md border border-border p-3">
                  <div className="text-[10px] tracking-widest uppercase opacity-60">Attempted</div>
                  <div className="font-display font-black text-2xl mt-1">{bulkResult.total}</div>
                </div>
                <div className="rounded-md border border-[#83B68B]/40 bg-[#83B68B]/5 p-3">
                  <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Delivered</div>
                  <div className="font-display font-black text-2xl mt-1 text-[#83B68B]" data-testid="bulk-resend-sent-count">{bulkResult.sent}</div>
                </div>
                <div className={`rounded-md border p-3 ${bulkResult.failed > 0 ? "border-amber-400/50 bg-amber-500/5" : "border-border"}`}>
                  <div className={`text-[10px] tracking-widest uppercase ${bulkResult.failed > 0 ? "text-amber-300" : "opacity-60"}`}>Failed</div>
                  <div className={`font-display font-black text-2xl mt-1 ${bulkResult.failed > 0 ? "text-amber-300" : ""}`}>{bulkResult.failed}</div>
                </div>
              </div>
              <div className="overflow-hidden rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
                    <tr>
                      <th className="text-left px-3 py-2">Name</th>
                      <th className="text-left px-3 py-2">Email</th>
                      <th className="text-left px-3 py-2">Email sent</th>
                      <th className="text-left px-3 py-2">Temp password</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bulkResult.results.map(r => (
                      <tr key={r.id} className="border-t border-border" data-testid={`bulk-resend-row-${r.id}`}>
                        <td className="px-3 py-2 font-medium">{r.name}</td>
                        <td className="px-3 py-2 opacity-80">{r.email}</td>
                        <td className="px-3 py-2">
                          {r.email_sent
                            ? <CheckCircle2 className="w-4 h-4 text-[#83B68B]" />
                            : <span className="text-[10px] text-amber-300" title={r.error || "delivery failed"}><X className="inline w-4 h-4" /> {r.error?.slice(0, 40) || "failed"}</span>}
                        </td>
                        <td className="px-3 py-2 font-mono text-xs select-all">{r.temp_password || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button onClick={() => setBulkResult(null)} className="btn-volt w-full py-3 rounded-md" data-testid="bulk-resend-done-btn">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
